import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  HStack,
  Tooltip,
  Text,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import analytics from '../../../../../segmentTracking';

const SearchControls = ({
  // Search props
  searchTerm: initialSearchTerm = '',
  onSearchChange,
  searchPlaceholder = 'Search...',

  // Items per page props
  itemsPerPage,
  onItemsPerPageChange,
  itemsPerPageOptions = [5, 10, 25, 50],

  // Additional props
  entityName = 'items',
  analyticsPrefix = 'academic',
  loading = false,

  // Optional props
  showSearch = true,
  showItemsPerPage = true,

  // Custom action slot
  children,
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    useState(initialSearchTerm);
  const [currentUser] = useState(JSON.parse(localStorage.getItem('user')));

  // Debounce search term with minimum 2 characters
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only search if term is empty or has at least 2 characters
      if (searchTerm === '' || searchTerm.length >= 2) {
        setDebouncedSearchTerm(searchTerm);

        // Track search events
        if (searchTerm.length >= 2) {
          analytics.track(`settings:${analyticsPrefix}:searched`, {
            searchTerm: searchTerm,
            searchLength: searchTerm.length,
            entityName: entityName,
            userRole: currentUser?.role,
            email: currentUser?.email,
          });
        }
      }
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [searchTerm, currentUser, analyticsPrefix, entityName]);

  // Notify parent component when debounced search term changes
  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearchChange]);

  // Handle immediate search input change
  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle clearing search
  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');

    // Track clear search action
    analytics.track(`settings:${analyticsPrefix}:searchcleared`, {
      previousSearchTerm: searchTerm,
      entityName: entityName,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    onItemsPerPageChange(newItemsPerPage);
    analytics.track(`settings:${analyticsPrefix}:itemsperpagechanged`, {
      itemsPerPage: newItemsPerPage,
      entityName: entityName,
      userRole: currentUser?.role,
      email: currentUser?.email,
    });
  };

  // Search status for UI feedback
  const searchStatus = useMemo(() => {
    if (searchTerm === '') {
      return { isSearching: false, message: '', showTooltip: false };
    } else if (searchTerm.length < 2) {
      return {
        isSearching: false,
        message: 'Type at least 2 characters to search...',
        showTooltip: true,
      };
    } else if (searchTerm !== debouncedSearchTerm) {
      return {
        isSearching: true,
        message: 'Searching...',
        showTooltip: false,
      };
    } else {
      return {
        isSearching: false,
        message: debouncedSearchTerm
          ? `Search results for "${debouncedSearchTerm}"`
          : '',
        showTooltip: false,
      };
    }
  }, [searchTerm, debouncedSearchTerm]);

  return (
    <Flex justifyContent="space-between" alignItems="center" mb={6}>
      <Box>
        <HStack spacing={4}>
          {showSearch && (
            <Box>
              <Tooltip
                label={searchStatus.message}
                isOpen={searchStatus.showTooltip}
                placement="bottom-start"
                bg="gray.700"
                color="white"
                fontSize="12px"
                px={3}
                py={2}
                borderRadius="6px"
                hasArrow
              >
                <InputGroup width="300px">
                  <Input
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    bg="white"
                    border="1px solid #E7E7E7"
                    borderRadius="8px"
                    _placeholder={{ color: '#757575' }}
                    isDisabled={loading}
                    pr="2.5rem" // Add padding for the clear button
                  />
                  {searchTerm && (
                    <InputRightElement>
                      <IconButton
                        aria-label="Clear search"
                        icon={<CloseIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={handleClearSearch}
                        isDisabled={loading}
                        _hover={{ bg: 'gray.100' }}
                        h="1.75rem"
                        w="1.75rem"
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
              </Tooltip>
              {searchStatus.message && !searchStatus.showTooltip && (
                <Text
                  fontSize="12px"
                  color={searchStatus.isSearching ? '#EF6506' : '#757575'}
                  mt={1}
                  ml={1}
                >
                  {searchStatus.message}
                </Text>
              )}
            </Box>
          )}

          {showItemsPerPage && (
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              width="150px"
              bg="white"
              border="1px solid #E7E7E7"
              borderRadius="8px"
              isDisabled={loading}
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option} per page
                </option>
              ))}
            </Select>
          )}
        </HStack>
      </Box>

      {/* Custom action buttons can be passed as children */}
      {children && <Box>{children}</Box>}
    </Flex>
  );
};

export default SearchControls;
